"use strict";

/**
 * Base class for filtering rules.
 *
 * @abstract
 */
export class FilterRule {
    op
    weight

    constructor(options) {
        this.weight = options.weight || 50;
        this.op = options.op || 'filter'
    }

    /**
     *
     * @param current jQuery set of already filtered elements
     * @param all jQuery set of all the elements.
     * @returns jQuery
     */
    process(current, all) {
        if (this.op === 'merge') {
            return $.merge(current, this.filter(all))
        } else {
            return this.filter(current)
        }
    }

    /**
     * @protected
     * @param list jQuery set of elements to process.
     * @returns jQuery
     */
    filter(list) {
        return list
    }
}

export class ClearFilterRule extends FilterRule {
    process(current, all) {
        return all;
    }
}

/**
 * Filter items with data-date in the given range.
 */
export class DateFilterRule extends FilterRule {
    constructor(options) {
        options.weight = options.weight || 80
        super(options);
        this.from = options.from || 0
        this.to = options.to || 0

        if (this.from && this.to && this.from > this.to) {
            this.to = [this.from, this.from = this.to][0]
        }
    }

    filter(list) {
        const rule = this
        return list.filter(function () {
            const date = $(this).data('date')
            // Date between from and to.
            return date && (!rule.from || rule.from <= date) && (!rule.to || date <= rule.to)
        })
    }
}

/**
 * Filter items with data-date and date-ends date range intersects anyhow with the given range.
 */
export class DateRangeFilterRule extends DateFilterRule {
    filter(list) {
        const rule = this
        return list.filter(function () {
            const start = $(this).data('date')
            const end = $(this).data('ends')
            // Starts sooner or in the range and ends in the range or after
            return start && end && (!rule.from || end >= rule.from) && (!rule.to || start < rule.to)
        })
    }
}

export class TextFilterRule extends FilterRule {

    constructor(options) {
        super(options)
        this.value = options.value
    }

    filter(list) {
        const rule = this
        return list.filter(function () {
            //try to use ":contains()"
            return $(this).find('h3').text().toLocaleLowerCase().includes(rule.value)
                || $(this).find('template.description').text().toLocaleLowerCase().includes(rule.value)
                || $(this).find('.badge').text().toLowerCase().includes(rule.value)
        })
    }
}

/**
 * Filters items by applying a subset of rules to it. Is used to create a structured filters.
 */
export class GroupFiltersRule extends FilterRule {
    #subRules = []

    addRule(rule) {
        if (rule instanceof FilterRule) {
            this.#subRules.push(rule)
        }
    }

    filter(list) {
        return GroupsListFilter.filterList(list, this.#subRules)
    }
}

export class DataAttrFilterRule extends FilterRule {
    name
    value

    constructor(options) {
        super(options)
        this.name = options.name
        this.value = options.value
    }

    filter(list) {
        const rule = this
        return list.filter(function () {
            const data = $(this).data(rule.name)
            // array with array, array with value, value with array, value with value
            if (rule.value instanceof Array) {
                return data instanceof Array
                    ? rule.value.filter(v => data.includes(v)).length > 0
                    : rule.value.includes(data);
            } else {
                return data instanceof Array
                    ? data.includes(rule.value.toString())
                    : data === rule.value.toString()
            }
        })
    }
}

export class FilterSource {
    #events = []
    enabled = true

    on(event, listener) {
        if (!this.#events[event]) {
            this.#events[event] = []
        }
        this.#events[event].push(listener)
        return this
    }

    trigger(event, data) {
        if (this.enabled && this.#events[event] instanceof Array) {
            this.#events[event].forEach(function (listener) {
                listener(data);
            })
        }
        return this
    }

    getActiveRules() {
        throw new Error("Not implemented")
    }
}

export class GroupsListFilter {

    constructor(options) {
        this.listItem = options.listItem || ''
        this.setContainer(options.container)
        this.sources = []
        if (options.sources) {
            this.addSource(options.sources)
        }
        this.emptyMessageBox = options.emptyMessageBox
        if (options.pagination && options.pagination.pageSize) {
            this.pagination = options.pagination
        } else {
            this.pagination = {enabled: false}
        }
        if (this.pagination.enabled) {
            this.pagination.currentLimit = this.pagination.startSize
                ? this.pagination.startSize
                : this.pagination.pageSize
            this.pagination.button.click(function () {
                this.more()
                return false
            }.bind(this))
            // todo: Move elsewhere. This class is NOT about widths or directions.
            this.container.scroll(function () {
                if (this.pagination.currentLimit < this.list.filter('.' + this.classId).length) {
                    let lastElement = this.list[this.pagination.currentLimit - 1].getBoundingClientRect();
                    let elementHalfWidth = lastElement.width / 2;
                    if (document.dir == 'rtl') {
                        if (lastElement.right > elementHalfWidth) {
                            this.more();
                        }
                    } else {
                        if (lastElement.left < elementHalfWidth) {
                            this.more();
                        }
                    }
                }
            }.bind(this))
        }
        this.enabled = typeof options.enabled === 'undefined'
            ? true
            : options.enabled
        this.events = []
        this.classId = Math.random().toString(36).replace('.', '')
    }

    setContainer(container) {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = null
        }
        this.container = container
        this.observer = new MutationObserver((mutations, observer) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    this.#regenerateList()
                }
            }
        })
        this.observer.observe(this.container[0], {childList: true})
        this.#regenerateList()
    }

    #regenerateList() {
        if (this.listItem) {
            this.list = this.container.children(this.listItem)
        } else {
            this.list = this.container.children()
        }
        this.update()
    }

    #show(el) {
        el.addClass(this.classId)
    }

    #hide(el) {
        el.removeClass(this.classId)
    }

    #updateEmptyMessage() {
        if (Array.isArray(this.emptyMesageBox)) {
            this.emptyMessageBox.forEach(function (element) {
                if (!element.list.is(':visible')) {
                    element.message.addClass('show')
                } else {
                    element.emptyMessageBox.removeClass('show')
                }
            })
        } else if (this.list.length > 0 && !this.list.is(':visible')) {
            this.emptyMessageBox.addClass('show')
        } else {
            this.emptyMessageBox.removeClass('show')
        }
    }

    #updateMoreButton() {
        if (this.pagination.enabled && this.pagination.currentLimit < this.list.filter('.' + this.classId).length) {
            this.pagination.button.removeClass('hidden')
        } else {
            if (this.pagination.button) {
                this.pagination.button.addClass('hidden')
            }
        }
    }

    #updateVisibility() {
        let list = this.list
        list.addClass('item-hide')
        this.getVisible(this.pagination.enabled).removeClass('item-hide')
    }

    /**
     * Adds rules source.
     *
     * @param source FilterSource|FilterSource[]
     */
    addSource(source) {
        if (Array.isArray(source)) {
            source.forEach(item => {
                this.addSource(item)
            })
        }
        if (source instanceof FilterSource) {
            this.sources.push(source)
            source.on('update', this.update.bind(this))
        }
    }

    /**
     * Removes all the filters.
     */
    clear() {
        this.#show(this.list)
        this.#updateVisibility()
        this.#updateEmptyMessage()
        this.#updateMoreButton()
        this.trigger('clear')
        return this
    }

    /**
     * Increases visible items limit.
     */
    more() {
        if (this.pagination.enabled) {
            this.pagination.currentLimit += this.pagination.pageSize
        }
        this.#updateVisibility()
        this.#updateMoreButton()
        this.trigger('more')
        return this
    }

    static filterList(list, rules) {
        let result = $([])
        // The issue is still here: we can't add tags to the full list, and we can't filter from empty list.
        // Also, no rules means "show all".
        if (rules.length === 0 || rules[0].op === 'filter') {
            result = list
        }
        rules.forEach(rule => {
            result = rule.process(result, list)
        })
        return result
    }

    getFiltered() {
        return GroupsListFilter.filterList(
            this.list,
            this.sources
                .flatMap(source => source.getActiveRules())
                .sort((a, b) => a.weight - b.weight)
        )
    }

    /**
     * Updates the list according to switches.
     */
    update() {
        if (this.enabled) {
            this.#hide(this.list)
            this.#show(this.getFiltered())
            this.#updateVisibility()
            this.#updateMoreButton()
            this.#updateEmptyMessage()
            this.trigger('update')
        }
        return this
    }

    on(event, listener) {
        if (!this.events[event]) {
            this.events[event] = []
        }
        this.events[event].push(listener)
        return this
    }

    trigger(event) {
        if (this.events[event] instanceof Array) {
            this.events[event].forEach((listener) => {
                listener(this);
            })
        }
        return this
    }

    getVisible(paginated = true) {
        return paginated
            ? this.list.filter('.' + this.classId).slice(0, this.pagination.currentLimit)
            : this.list.filter('.' + this.classId)
    }
}
